import { useField } from 'formik';

import { getQuestionNumber } from '../../../util/questions';
import {
  getOptionOptions,
  getQuestionOptions,
} from '../../../util/formOptions';
import { Question, QUESTION_TYPE } from '../../../types/domainModels';
import { QuestionFormData, ReactSelectValue } from '../../../types/forms';

import CheckboxWithTooltip from '../../common/forms/CheckboxWithTooltip';
import FormSearchSelectInput from '../../common/forms/FormSearchSelectInput';
import Select from '../../common/forms/Select';

interface CarryOverTotalProps {
  question: Question | undefined;
  questions: Question[];
}

const CarryOverTotal = ({ question, questions }: CarryOverTotalProps) => {
  const [{ value: carryOverTotalEnabled }, , carryOverTotalEnabledHelpers] =
    useField<QuestionFormData['allocationCarryOverTotalEnabled']>(
      'allocationCarryOverTotalEnabled',
    );
  const [, , carryOverTotalOptionIdHelpers] = useField<
    QuestionFormData['allocationCarryOverTotalOptionId']
  >('allocationCarryOverTotalOptionId');
  const [, , carryOverTotalQuestionHelpers] = useField<
    QuestionFormData['allocationCarryOverTotalQuestion']
  >('allocationCarryOverTotalQuestion');
  const [, , allocationTotalHelpers] =
    useField<QuestionFormData['allocationTotal']>('allocationTotal');
  const [, , allocationMaxPerOptionHelpers] = useField<
    QuestionFormData['allocationMaxPerOption']
  >('allocationMaxPerOption');

  const isEnabled = !!carryOverTotalEnabled;

  const firstActiveQuestionId = questions.find((q) => q.isActive)?.id;
  const isDisabledFirstQuestion =
    !isEnabled &&
    (questions.length === 0 || firstActiveQuestionId === question?.id);

  return (
    <>
      <CheckboxWithTooltip
        checked={isEnabled}
        disabled={isDisabledFirstQuestion}
        label="Carry Over Total"
        name="carryOverTotalEnabled"
        onChange={() => {
          if (isEnabled) {
            // Toggling OFF: clear carry-over fields
            carryOverTotalEnabledHelpers.setValue(false);
            carryOverTotalOptionIdHelpers.setValue(null);
            carryOverTotalQuestionHelpers.setValue(null);
          } else {
            // Toggling ON: persist enabled state and clear total/max fields
            carryOverTotalEnabledHelpers.setValue(true);
            allocationTotalHelpers.setValue('');
            allocationMaxPerOptionHelpers.setValue('');
          }
        }}
        tooltip={
          <div>
            <p>
              This feature allows you to use an option value from a previous
              question as the allocation total.
            </p>
            {isDisabledFirstQuestion && (
              <p className="mt-4 font-bold">
                Disabled: Cannot carry over total from a previous question when
                this is the first active question.
              </p>
            )}
          </div>
        }
      />
      {isEnabled && (
        <CarryOverTotalConfiguration
          question={question}
          questions={questions}
        />
      )}
    </>
  );
};

export default CarryOverTotal;

const CarryOverTotalConfiguration = ({
  question,
  questions,
}: {
  question: Question | undefined;
  questions: Question[];
}) => {
  const [{ value: carryOverTotalOptionId }, , carryOverTotalOptionIdHelpers] =
    useField<QuestionFormData['allocationCarryOverTotalOptionId']>(
      'allocationCarryOverTotalOptionId',
    );
  const [{ value: carryOverTotalQuestion }] = useField<
    QuestionFormData['allocationCarryOverTotalQuestion']
  >('allocationCarryOverTotalQuestion');

  const questionId = question?.id;
  const isCurrentQuestionDemographic = question?.isDemographic;
  const questionNumber = getQuestionNumber({ question, questions });

  const carryOverQuestionOptions = getQuestionOptions({
    filterFn: (q: Question) => {
      const isNotCurrentQuestion = q.id !== questionId;
      const isAllocation =
        q.questionTypeId === QUESTION_TYPE.ALLOCATION && q.options.length > 0;
      const isPreviousQuestion = isCurrentQuestionDemographic
        ? q.sort < questionNumber
        : q.isDemographic || q.sort < questionNumber;

      return isNotCurrentQuestion && isAllocation && isPreviousQuestion;
    },
    questions,
  });

  const selectedQuestion = carryOverTotalQuestion?.value;
  const optionOptions = selectedQuestion
    ? getOptionOptions({ options: selectedQuestion.options })
    : [];

  // Derive the selected option ReactSelectValue from the stored ID
  const selectedOptionValue =
    carryOverTotalOptionId && selectedQuestion
      ? (optionOptions.find((o) => o.value.id === carryOverTotalOptionId) ??
        null)
      : null;

  return (
    <div>
      <div className="flex p-2 space-x-4">
        <span className="flex-shrink-0 text-xs">
          from
          <br />
          question
        </span>
        <div className="flex-grow">
          <FormSearchSelectInput
            name="allocationCarryOverTotalQuestion"
            onChange={() => {
              // Clear the option when the question changes
              carryOverTotalOptionIdHelpers.setValue(null);
            }}
            options={carryOverQuestionOptions}
          />
        </div>
      </div>
      {selectedQuestion && (
        <div className="flex p-2 space-x-4">
          <span className="flex-shrink-0 text-xs">option</span>
          <div className="flex-grow">
            <Select
              onChange={(
                option: ReactSelectValue<Question['options'][number]> | null,
              ) => {
                carryOverTotalOptionIdHelpers.setValue(
                  option?.value.id ?? null,
                );
              }}
              options={optionOptions}
              value={selectedOptionValue}
            />
          </div>
        </div>
      )}
    </div>
  );
};
